import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, CreditCard, Calendar, Clock, Package, LogOut, ChevronRight, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-emerald-100 text-emerald-700',
  preparing: 'bg-blue-100 text-blue-700',
  ready: 'bg-purple-100 text-purple-700',
  delivered: 'bg-stone-100 text-stone-600',
  cancelled: 'bg-red-100 text-red-700',
  active: 'bg-emerald-100 text-emerald-700'
};

const statusLabels = {
  pending: 'Pendiente',
  paid: 'Pagado',
  preparing: 'Preparando',
  ready: 'Listo',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
  active: 'Activa'
};

export default function Dashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');

  useEffect(() => {
    // Skip if coming from AuthCallback with user data
    if (location.state?.user) {
      return;
    }

    if (!authLoading && !user) {
      navigate('/');
      return;
    }
  }, [user, authLoading, navigate, location.state]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        const [ordersRes, subRes] = await Promise.all([
          fetch(`${API}/orders`, { credentials: 'include' }),
          fetch(`${API}/subscriptions/me`, { credentials: 'include' })
        ]);

        if (ordersRes.ok) {
          const ordersData = await ordersRes.json();
          setOrders(ordersData);
        }

        if (subRes.ok) {
          const subData = await subRes.json();
          setSubscription(subData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-700 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="dashboard">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center overflow-hidden">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-emerald-700" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-stone-900">Hola, {user.name.split(' ')[0]}</h1>
                <p className="text-stone-500">{user.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="rounded-full border-stone-200"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>

          {/* Subscription Card */}
          {subscription && (
            <div className="bg-gradient-to-r from-emerald-700 to-emerald-600 rounded-3xl p-6 md:p-8 mb-8 text-white">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <Badge className="bg-white/20 text-white border-0 mb-3">
                    <CreditCard className="w-3 h-3 mr-1" />
                    Suscripción Activa
                  </Badge>
                  <h2 className="text-2xl font-bold mb-2">{subscription.plan_name}</h2>
                  <p className="text-emerald-100">
                    {subscription.meals_remaining} comidas restantes de {subscription.meals_per_month}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-emerald-100 text-sm mb-1">Válido hasta</p>
                  <p className="text-xl font-bold">
                    {subscription.end_date ? format(new Date(subscription.end_date), 'dd MMM yyyy', { locale: es }) : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="mt-6">
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all duration-500"
                    style={{ width: `${(subscription.meals_remaining / subscription.meals_per_month) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Link to="/menu" className="bg-white rounded-2xl p-5 border border-stone-100 hover:border-emerald-200 hover:shadow-md transition-all group" data-testid="quick-action-menu">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-emerald-200 transition-colors">
                <ShoppingBag className="w-6 h-6 text-emerald-700" />
              </div>
              <p className="font-semibold text-stone-900">Ver Menú</p>
              <p className="text-sm text-stone-500">Ordenar ahora</p>
            </Link>
            <Link to="/subscriptions" className="bg-white rounded-2xl p-5 border border-stone-100 hover:border-emerald-200 hover:shadow-md transition-all group" data-testid="quick-action-plans">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-orange-200 transition-colors">
                <CreditCard className="w-6 h-6 text-orange-600" />
              </div>
              <p className="font-semibold text-stone-900">Planes</p>
              <p className="text-sm text-stone-500">Suscripciones</p>
            </Link>
            <Link to="/cart" className="bg-white rounded-2xl p-5 border border-stone-100 hover:border-emerald-200 hover:shadow-md transition-all group" data-testid="quick-action-cart">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3 group-hover:bg-blue-200 transition-colors">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <p className="font-semibold text-stone-900">Carrito</p>
              <p className="text-sm text-stone-500">Ver carrito</p>
            </Link>
            <div className="bg-white rounded-2xl p-5 border border-stone-100">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <p className="font-semibold text-stone-900">Apartados</p>
              <p className="text-sm text-stone-500">Reservaciones</p>
            </div>
          </div>

          {/* Orders */}
          <div className="bg-white rounded-3xl border border-stone-100 overflow-hidden">
            <div className="p-6 border-b border-stone-100">
              <h2 className="text-xl font-bold text-stone-900">Mis Pedidos</h2>
            </div>
            
            {orders.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag className="w-8 h-8 text-stone-400" />
                </div>
                <p className="text-stone-500 mb-4">Aún no tienes pedidos</p>
                <Link to="/menu">
                  <Button className="bg-emerald-700 hover:bg-emerald-800 rounded-full" data-testid="first-order-btn">
                    Hacer mi primer pedido
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {orders.slice(0, 5).map(order => (
                  <div key={order.order_id} className="p-6 hover:bg-stone-50 transition-colors" data-testid={`order-${order.order_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center">
                          {order.order_type === 'reservation' ? (
                            <Calendar className="w-6 h-6 text-stone-600" />
                          ) : (
                            <ShoppingBag className="w-6 h-6 text-stone-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-stone-900">
                            Pedido #{order.order_id.slice(-8).toUpperCase()}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-stone-500">
                            <Clock className="w-3 h-3" />
                            {format(new Date(order.created_at), "dd MMM yyyy, HH:mm", { locale: es })}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <p className="font-bold text-stone-900">${order.total.toFixed(2)}</p>
                          <Badge className={`${statusColors[order.status]} border-0 text-xs`}>
                            {statusLabels[order.status]}
                          </Badge>
                        </div>
                        <ChevronRight className="w-5 h-5 text-stone-400" />
                      </div>
                    </div>
                    {order.order_type === 'reservation' && order.reservation_date && (
                      <div className="mt-3 ml-16">
                        <Badge variant="outline" className="border-orange-200 text-orange-700">
                          <Calendar className="w-3 h-3 mr-1" />
                          Apartado para: {format(new Date(order.reservation_date), "dd MMM yyyy", { locale: es })}
                        </Badge>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
